import { useMutation, type UseMutationOptions } from "@tanstack/react-query";
import {
  type Chain,
  type HttpTransport,
  type PrivateKeyAccount,
  type TypedDataParameter,
  type WalletClient,
} from "viem";

import { errorMessages } from "#/config/errorMessages";
import { type ChainId } from "#/config/web3/contractAddresses";
import { useWalletClient } from "#/hooks/useWalletClient";
import { getPublicClient } from "#/utils/getPublicClient";
import { getSignatureDeadline } from "#/utils/getSignatureDeadline";
import { EIP712Domain, withSignatureErrorHandler } from "#/utils/signature";

import { createSession, isApiKeyCreated } from "../kyan.service";
import { type CreateSessionRequest } from "../kyan.types";
import { defaultApiResponseHandler } from "../kyan.utils";
import { useCreateApiKey } from "./useCreateApiKey";

export const OneClickSignature: TypedDataParameter[] = [
  { name: "deadline", type: "uint256" },
  { name: "user", type: "address" },
  { name: "bindToIp", type: "bool" },
];

const signCreateSession = async (
  sessionRequest: Omit<
    CreateSessionRequest,
    "signature" | "signature_deadline"
  >,
  walletClient: WalletClient<HttpTransport, Chain, PrivateKeyAccount>,
): Promise<CreateSessionRequest> => {
  const signatureDeadline = getSignatureDeadline();

  return {
    ...sessionRequest,
    signature_deadline: signatureDeadline,
    signature: await walletClient.signTypedData({
      domain: EIP712Domain[walletClient.chain.id as ChainId],
      types: { OneClickSignature },
      primaryType: "OneClickSignature",
      message: {
        deadline: BigInt(signatureDeadline),
        user: sessionRequest.user,
        bindToIp: sessionRequest.bind_to_ip,
      },
    }),
  };
};

const validateCreateSessionSignature = async (
  payload: CreateSessionRequest,
  chainId: ChainId,
): Promise<boolean> => {
  const publicClient = getPublicClient(chainId);

  return await publicClient.verifyTypedData({
    address: payload.user,
    domain: EIP712Domain[chainId],
    types: { OneClickSignature },
    primaryType: "OneClickSignature",
    message: {
      deadline: BigInt(payload.signature_deadline),
      user: payload.user,
      bindToIp: payload.bind_to_ip,
    },
    signature: payload.signature,
  });
};

export const useCreateSession = (options?: UseMutationOptions<string>) => {
  const { walletClient } = useWalletClient();
  const { mutateAsync: createApiKey } = useCreateApiKey(true);

  return useMutation({
    ...options,
    mutationFn: async () => {
      if (!walletClient) {
        throw new Error(errorMessages.WALLET_CLIENT_UNAVAILABLE);
      }

      const account = walletClient.account?.address;
      if (!account) {
        throw new Error("Account is not available");
      }

      const hasKey = await isApiKeyCreated();

      if (!hasKey) {
        await createApiKey();
      }

      const signedSession = await withSignatureErrorHandler(async () =>
        signCreateSession(
          {
            user: account,
            bind_to_ip: true,
          },
          walletClient as WalletClient<HttpTransport, Chain, PrivateKeyAccount>,
        ),
      );

      const isSignatureValid = await validateCreateSessionSignature(
        signedSession,
        walletClient.chain?.id as ChainId,
      );

      if (!isSignatureValid) {
        throw new Error("Invalid one-click session signature");
      }

      return defaultApiResponseHandler({
        serviceName: "Create Session",
        payload: signedSession,
        service: createSession,
      });
    },
  });
};
